import React, { useContext, useEffect, useState, useMemo } from 'react'
import SearchLocationContext from '../../context/SearchLocationContext'
import LocationUtilities from '../../utilities/LocationUtilities'
import LocationAddress from './LocationAddress/LocationAddress'
import LocationHour from './LocationHour/LocationHour'
import styles from './LocationDetails.module.scss'
import { ReactComponent as LeftArrow } from '../../assets/media/icons/icon-arrow-left.svg'
import { ReactComponent as Phone } from '../../assets/media/icons/icon-phone.svg'
import { ReactComponent as Clock } from '../../assets/media/icons/icon-clock.svg'
import { ReactComponent as Star } from '../../assets/media/icons/icon-star.svg'
import { ReactComponent as DrivingSign } from '../../assets/media/icons/icon-directions.svg'
import { ReactComponent as ChevronDown } from '../../assets/media/icons/chevron-down.svg'
import Spacer from '../Spacer/Spacer'
import LoadingDots from '../LoadingDots/LoadingDots'
import ErrorContainer from '../ErrorContainer/ErrorContainer'
import LocationBadges from '../LocationBadges/LocationBadges'
import ApplicationContext from '../../context/ApplicationContext'
import { useFocusTrap } from '../../hooks'
import FormatUtilities from '../../utilities/FormatUtilities'
import { LocationDetailsTabs } from '../../constants'
import LocationFeatures from './LocationFeatures/LocationFeatures'
import EventsBar from '../EventsBar/EventsBar'
import EventsList from '../EventsList/EventsList'

const LocationDetails = () => {
    const [searchLocationData, setSearchLocationData] = useContext(SearchLocationContext)
    const [applicationData, setApplicationData] = useContext(ApplicationContext)
    const [localizations] = useState(applicationData.localizations)
    const [locationInfo, setLocationInfo] = useState()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [address, setAddress] = useState(null)
    const [phone, setPhone] = useState(null)
    const [hours, setHours] = useState(null)
    const [certifications, setCertifications] = useState(null)
    const [localLocationId, setLocalLocationId] = useState(null)
    const [isOpen, setIsOpen] = useState(false)
    const [nextOpen, setNextOpen] = useState('')
    const [openDetails, setOpenDetails] = useState({
        hours: false,
        certs: false,
    })
    const containerRef = useFocusTrap(true)

    const locationInfoWithEvents = useMemo(() => {
        if (locationInfo) {
            return {
                ...locationInfo,
                events: searchLocationData.locationList.find(location => location.locationId === locationInfo.locationId)?.events || [],
            }
        }

        return null
    }, [locationInfo, searchLocationData.locationList])

    useEffect(() => {
        if (searchLocationData.activeLocationId !== localLocationId) {
            setLoading(true)
            setLocalLocationId(searchLocationData.activeLocationId)
            LocationUtilities.getLocationData(searchLocationData.activeLocationId)
                .then(response => {
                    setLoading(false)
                    setLocationInfo(response?.data?.data)
                })
                .catch(() => {
                    setLoading(false)
                    setError(true)
                })
        }
    }, [searchLocationData])

    useEffect(() => {
        setAddress({
            state: locationInfo?.address?.state,
            compliment: locationInfo?.address?.compliment,
            postalCode: locationInfo?.address?.postalCode,
            street: locationInfo?.address?.street,
            city: locationInfo?.address?.city,
        })

        setHours({
            mondayStart: locationInfo?.locationHours?.mondayStart,
            mondayEnd: locationInfo?.locationHours?.mondayEnd,
            tuesdayStart: locationInfo?.locationHours?.tuesdayStart,
            tuesdayEnd: locationInfo?.locationHours?.tuesdayEnd,
            wednesdayStart: locationInfo?.locationHours?.wednesdayStart,
            wednesdayEnd: locationInfo?.locationHours?.wednesdayEnd,
            thursdayStart: locationInfo?.locationHours?.thursdayStart,
            thursdayEnd: locationInfo?.locationHours?.thursdayEnd,
            fridayStart: locationInfo?.locationHours?.fridayStart,
            fridayEnd: locationInfo?.locationHours?.fridayEnd,
            saturdayStart: locationInfo?.locationHours?.saturdayStart,
            saturdayEnd: locationInfo?.locationHours?.saturdayEnd,
            sundayStart: locationInfo?.locationHours?.sundayStart,
            sundayEnd: locationInfo?.locationHours?.sundayEnd,
        })

        if (locationInfo?.locationHours?.timeZone) {
            const { locationIsOpen, nextOpenClose } = FormatUtilities.formatWorkingHoursRow(locationInfo.locationHours, localizations)
            setIsOpen(locationIsOpen)
            setNextOpen(nextOpenClose)
        }

        setPhone(locationInfo?.contact?.formattedNumber)

        if (locationInfo?.certifications.length > 0) {
            let certDict = []
            locationInfo.certifications.forEach(c => {
                if (!certDict[c.filterOrder]) {
                    certDict[c.filterOrder] = [c]
                } else {
                    certDict[c.filterOrder].push(c)
                }
            })
            setCertifications(certDict)
        }
    }, [locationInfo])

    const backToListing = () => {
        setSearchLocationData(prevState => {
            return {
                ...prevState,
                ...prevState.prevMapState,
                activeLocationId: null,
                highlightedLocationId: null,
            }
        })
        setApplicationData(prev => {
            return {
                ...prev,
                activeLocationTab: LocationDetailsTabs.Null,
                overlay: false,
            }
        })

        // Note: No need to call handleSearch - prevMapState already contains the previous location list
    }

    const backToDetails = () => {
        setApplicationData(prev => {
            return {
                ...prev,
                activeLocationTab: LocationDetailsTabs.Null,
            }
        })
    }

    return (
        <div className={styles.containerWrapper}>
            <div
                ref={containerRef}
                className={styles.container}
                role="dialog"
                aria-label={localizations.fittingLocationFound}
                aria-modal="true"
            >
                <div className={styles.header}>
                    <button className={styles.backButton} onClick={backToListing} type='button' aria-label={localizations.backToResults}>
                        <LeftArrow aria-hidden='true' focusable='false' role='img' />
                        <div className={styles.header_text}>{localizations.backToResults}</div>
                    </button>
                </div>
                <Spacer border={true} />
                <div className={styles.contentBoxWrapper}>
                    <div className={styles.contentBox}>
                        {loading ? (
                            <LoadingDots text={`${localizations.loadingDetails}...`} />
                        ) : locationInfo ? (
                            <>
                                <div className={styles.contentBox_header}>
                                    <h2 className={styles.locationTitle}>{locationInfo?.name}</h2>
                                </div>
                                <div className={styles.contentBox_content}>
                                    <div className={styles.contentBox_row}>
                                        <LocationBadges
                                            certifiedFitter={locationInfo?.badges?.BadgeCertifiedFitter}
                                            pldFitter={locationInfo?.badges?.BadgeCustomFittingExperience}
                                            fitterOfTheYear={locationInfo?.badges?.BadgeFitterOfTheYear}
                                            fittingExperience={locationInfo?.badges?.BadgeFittingExperience}
                                        />
                                    </div>
                                    {address !== null && (
                                        <div className={`${styles.contentBox_row} ${styles.noCursor}`}>
                                            <LocationAddress address={address} showIcons />
                                        </div>
                                    )}
                                    {locationInfo?.address?.latitude && locationInfo?.address?.longitude && (
                                        <>
                                            <Spacer />
                                            <div className={styles.contentBox_row}>
                                                <div className={styles.contentBox_text}>
                                                    <DrivingSign />
                                                    <a
                                                        href={LocationUtilities.generateDrivingDirectionsUrl(
                                                            locationInfo?.address?.latitude,
                                                            locationInfo?.address?.longitude
                                                        )}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                    >
                                                        {localizations.drivingDirections}
                                                    </a>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {phone && (
                                        <>
                                            <Spacer />
                                            <div className={styles.contentBox_row}>
                                                <div className={styles.contentBox_text}>
                                                    <Phone />
                                                    <a href={`tel:${phone}`}>{phone}</a>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {locationInfo?.locationHours?.displayHours && (
                                        <>
                                            <Spacer />
                                            <button
                                                type="button"
                                                className={styles.contentBox_row}
                                                onClick={() => setOpenDetails({ ...openDetails, hours: !openDetails.hours })}
                                                aria-expanded={openDetails.hours}
                                            >
                                                <div className={styles.contentBox_text}>
                                                    <Clock />
                                                    <div className={styles.workingHours}>
                                                        <span className={isOpen ? styles.workingHours_open : styles.workingHours_closed}>
                                                            {isOpen ? localizations.open : localizations.closed}
                                                        </span>
                                                        {isOpen && (
                                                            <>
                                                                <span className={styles.workingHours_dot} />
                                                                <span>{nextOpen}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <ChevronDown className={`${styles.contentBox_icon} ${openDetails.hours ? styles.open : ''}`} aria-hidden="true" />
                                            </button>
                                            <div className={`${styles.contentBox_info} ${openDetails.hours ? styles.open : ''}`}>
                                                <LocationHour hour={hours} handleGoBack={backToDetails} handleClose={backToListing} />
                                            </div>
                                        </>
                                    )}
                                    {certifications?.length > 0 && (
                                        <>
                                            <Spacer />
                                            <button
                                                type="button"
                                                className={styles.contentBox_row}
                                                onClick={() => setOpenDetails({ ...openDetails, certs: !openDetails.certs })}
                                                aria-expanded={openDetails.certs}
                                            >
                                                <div className={styles.contentBox_text}>
                                                    <Star />
                                                    <span className={styles.contentBox_button_text}>{localizations.featuresAndAmenities}</span>
                                                </div>
                                                <ChevronDown className={`${styles.contentBox_icon} ${openDetails.certs ? styles.open : ''}`} aria-hidden="true" />
                                            </button>
                                            <div className={`${styles.contentBox_info} ${openDetails.certs ? styles.open : ''}`}>
                                                <LocationFeatures
                                                    certifications={certifications || []}
                                                    handleGoBack={backToDetails}
                                                    handleClose={backToListing}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </>
                        ) : (
                            error && <ErrorContainer errorMessage={localizations.errors.locationDetailsCanNotBeDislayed} />
                        )}
                    </div>
                    <Spacer />
                    {locationInfoWithEvents && locationInfoWithEvents.events.length > 0 && (
                        <div className={styles.bottomBar}>
                            <EventsBar item={locationInfoWithEvents} localizations={localizations} showButton={true} />
                        </div>
                    )}
                </div>
                <div className={`${styles.tabWrapper} ${applicationData?.activeLocationTab !== null ? styles.tabOpen : ''}`}>
                    <div className={styles.tabContainer}>
                        {applicationData?.activeLocationTab === LocationDetailsTabs.Hours && (
                            <LocationHour hour={hours} handleGoBack={backToDetails} handleClose={backToListing} />
                        )}
                        {applicationData?.activeLocationTab === LocationDetailsTabs.Features && (
                            <LocationFeatures certifications={certifications || []} handleGoBack={backToDetails} handleClose={backToListing} />
                        )}
                    </div>
                </div>
                <EventsList />
            </div>
        </div>
    )
}

export default LocationDetails
